/**
 * Online-payment purchase: stash cart snapshot before redirect,
 * fire purchase only after backend StatusOrder confirms payment_status=paid.
 * URL params (payment=success, paid_order*) are hints only — never trust them for paid.
 */

import axiosInstance from "../store/request";
import { trackPurchase as defaultTrackPurchase, toNumber } from "./dataLayer";

const PENDING_KEY = "vlv_ga4_pending_purchases";
const TERMINAL_FAIL = new Set(["failed", "cancelled", "canceled", "expired"]);
const PENDING_LIKE = new Set(["pending", "processing", "unpaid"]);

const DEFAULT_MAX_RETRIES = 4;
const DEFAULT_RETRY_DELAY_MS = 1500;

function canUseDom() {
  return typeof window !== "undefined";
}

function storageGet(store, key) {
  if (!store) return null;
  try {
    return store.getItem(key);
  } catch {
    return null;
  }
}

function storageSet(store, key, value) {
  if (!store) return;
  try {
    store.setItem(key, value);
  } catch {
    /* ignore quota / private mode */
  }
}

function readPending() {
  if (!canUseDom()) return [];
  const sources = [window.sessionStorage, window.localStorage];
  for (const store of sources) {
    try {
      const raw = storageGet(store, PENDING_KEY);
      const arr = raw ? JSON.parse(raw) : [];
      if (Array.isArray(arr) && arr.length) return arr;
    } catch {
      /* try next */
    }
  }
  return [];
}

function writePending(list) {
  if (!canUseDom()) return;
  const payload = JSON.stringify((list || []).slice(-30));
  storageSet(window.sessionStorage, PENDING_KEY, payload);
  storageSet(window.localStorage, PENDING_KEY, payload);
}

export function clearPendingPurchases() {
  writePending([]);
}

/**
 * Save ecommerce snapshot before redirecting to a payment gateway.
 * Kept in sessionStorage + localStorage so return in a new tab still works.
 */
export function stashPendingPurchase({
  order_id,
  order_number,
  value,
  items,
  payment_type,
  shipping_tier,
  coupon,
  shipping,
  tax,
} = {}) {
  const tid = order_number != null ? String(order_number) : "";
  const oid = order_id != null ? String(order_id) : "";
  if (!tid && !oid) return false;
  if (!Array.isArray(items) || !items.length) return false;

  const list = readPending().filter(
    (p) =>
      String(p.order_number || "") !== tid && String(p.order_id || "") !== oid
  );
  list.push({
    order_id: oid || undefined,
    order_number: tid || undefined,
    value: toNumber(value),
    items,
    payment_type: payment_type || undefined,
    shipping_tier: shipping_tier || undefined,
    coupon: coupon || undefined,
    shipping: shipping != null ? toNumber(shipping) : undefined,
    tax: tax != null ? toNumber(tax) : undefined,
    created_at: Date.now(),
  });
  writePending(list);
  return true;
}

export function isPaidStatus(payload) {
  if (!payload || typeof payload !== "object") return false;
  // Never infer paid from URL — only structured API fields.
  if (payload.payment_status === "paid") return true;
  if (Number(payload.is_paid) === 1 && payload.payment_status !== "cancelled") {
    return true;
  }
  return false;
}

export function isFailedStatus(payload) {
  if (!payload || typeof payload !== "object") return false;
  const st = String(payload.payment_status || "").toLowerCase();
  return TERMINAL_FAIL.has(st);
}

export function isPendingStatus(payload) {
  if (!payload || typeof payload !== "object") return true;
  if (isPaidStatus(payload) || isFailedStatus(payload)) return false;
  const st = String(payload.payment_status || "").toLowerCase();
  if (!st) return Number(payload.is_paid) !== 1;
  return PENDING_LIKE.has(st) || st === "unpaid";
}

async function defaultFetchOrderPaymentStatus({ order_id, order_number }) {
  const key = order_id || order_number;
  if (key == null || key === "") return null;

  const body = {
    token: process.env.REACT_APP_TOKEN,
    login: process.env.REACT_APP_LOGIN,
    password: process.env.REACT_APP_PASSWORD,
  };

  try {
    const res = await axiosInstance.post(
      `status/order/${encodeURIComponent(key)}`,
      body
    );
    return res?.data || null;
  } catch {
    return null;
  }
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function normalizePathname(pathname) {
  if (!pathname) return "/";
  // Collapse accidental // while keeping a single leading slash.
  const collapsed = String(pathname).replace(/\/{2,}/g, "/");
  return collapsed || "/";
}

function readReturnHints() {
  try {
    const params = new URLSearchParams(window.location.search || "");
    return {
      payment: (params.get("payment") || "").toLowerCase(),
      paidOrder: params.get("paid_order"),
      paidOrderId: params.get("paid_order_id"),
    };
  } catch {
    return { payment: "", paidOrder: null, paidOrderId: null };
  }
}

function matchesHint(entry, hints) {
  if (!hints) return false;
  if (hints.paidOrder && String(entry.order_number) === String(hints.paidOrder)) {
    return true;
  }
  if (hints.paidOrderId && String(entry.order_id) === String(hints.paidOrderId)) {
    return true;
  }
  return false;
}

function stripPaymentParamsFromUrl() {
  try {
    const url = new URL(window.location.href);
    let changed = false;
    ["paid_order", "paid_order_id", "payment"].forEach((k) => {
      if (url.searchParams.has(k)) {
        url.searchParams.delete(k);
        changed = true;
      }
    });
    const pathname = normalizePathname(url.pathname);
    if (pathname !== url.pathname) {
      url.pathname = pathname;
      changed = true;
    }
    if (changed) {
      // Use native replaceState to avoid re-entrancy via history patch.
      const native =
        window.__vlvNativeReplaceState || window.history.replaceState.bind(window.history);
      native({}, document.title, pathname + url.search + url.hash);
    }
  } catch {
    /* ignore */
  }
}

/**
 * Check pending online orders; fire purchase only when backend says paid.
 * @param {object} [options]
 * @param {Function} [options.fetchStatus]
 * @param {Function} [options.trackPurchaseFn]
 * @param {Function} [options.sleepFn]
 * @param {number} [options.maxRetries]
 * @param {number} [options.retryDelayMs]
 */
export async function flushPendingPurchases(options = {}) {
  if (!canUseDom()) return [];

  const fetchStatus = options.fetchStatus || defaultFetchOrderPaymentStatus;
  const trackPurchaseFn = options.trackPurchaseFn || defaultTrackPurchase;
  const sleepFn = options.sleepFn || sleep;
  const maxRetries =
    options.maxRetries != null ? options.maxRetries : DEFAULT_MAX_RETRIES;
  const retryDelayMs =
    options.retryDelayMs != null ? options.retryDelayMs : DEFAULT_RETRY_DELAY_MS;

  const hints = readReturnHints();
  let pending = readPending();

  // payment=failed|cancelled in URL → drop matching pending without purchase.
  if (
    hints.payment &&
    TERMINAL_FAIL.has(hints.payment) &&
    (hints.paidOrder || hints.paidOrderId)
  ) {
    pending = pending.filter((p) => !matchesHint(p, hints));
    writePending(pending);
    stripPaymentParamsFromUrl();
    if (!pending.length) return [];
  }

  // payment=success alone never creates a purchase — need a stashed pending entry.
  if (!pending.length) {
    if (hints.payment || hints.paidOrder || hints.paidOrderId) {
      stripPaymentParamsFromUrl();
    }
    return [];
  }

  // Prefer checking the order referenced in return URL first (hint only).
  let ordered = pending;
  if (hints.paidOrder || hints.paidOrderId) {
    ordered = [
      ...pending.filter((p) => matchesHint(p, hints)),
      ...pending.filter((p) => !matchesHint(p, hints)),
    ];
  }

  const remaining = [];
  const fired = [];

  for (const entry of ordered) {
    if (entry.created_at && Date.now() - Number(entry.created_at) > 7 * 864e5) {
      continue;
    }

    let status = null;
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      status = await fetchStatus({
        order_id: entry.order_id,
        order_number: entry.order_number,
      });

      if (!status || status.error === 1) {
        // Network / not found — keep pending for a later page load.
        remaining.push(entry);
        status = null;
        break;
      }

      if (isPaidStatus(status)) break;

      if (isFailedStatus(status)) {
        // Terminal failure — drop pending, no purchase.
        status = { __drop: true };
        break;
      }

      if (isPendingStatus(status) && attempt < maxRetries) {
        await sleepFn(retryDelayMs);
        continue;
      }

      // Still pending after retries — keep for later (visibility / refresh).
      remaining.push(entry);
      status = null;
      break;
    }

    if (!status || status.__drop) {
      continue;
    }

    if (!isPaidStatus(status)) {
      remaining.push(entry);
      continue;
    }

    // Prefer verified order payload from API; fall back to stashed cart snapshot.
    const items =
      Array.isArray(status.items) && status.items.length
        ? status.items
        : Array.isArray(entry.items) && entry.items.length
          ? entry.items
          : [];

    if (!items.length) {
      // Paid but cannot build purchase — drop to avoid infinite retries.
      continue;
    }

    const tid = String(
      status.order_number || entry.order_number || entry.order_id || ""
    ).trim();
    if (!tid) continue;

    const ok = trackPurchaseFn({
      transaction_id: tid,
      value:
        status.value != null
          ? toNumber(status.value)
          : entry.value != null
            ? toNumber(entry.value)
            : undefined,
      tax: entry.tax,
      shipping: entry.shipping,
      coupon: entry.coupon,
      payment_type: status.payment_type || entry.payment_type,
      shipping_tier: entry.shipping_tier,
      items,
    });

    if (ok) {
      fired.push(tid);
    }
    // Paid (or already deduped) — remove from pending.
  }

  writePending(remaining);
  stripPaymentParamsFromUrl();
  return fired;
}

export function initPendingPurchaseFlush() {
  if (!canUseDom()) return;
  if (window.__vlvPendingPurchaseInit) return;
  window.__vlvPendingPurchaseInit = true;

  window.__vlvNativeReplaceState = window.history.replaceState.bind(
    window.history
  );
  window.__vlvNativePushState = window.history.pushState.bind(window.history);

  let flushing = false;
  const run = () => {
    if (flushing) return;
    flushing = true;
    flushPendingPurchases()
      .catch(() => {})
      .finally(() => {
        flushing = false;
      });
  };

  if (document.readyState === "complete") run();
  else window.addEventListener("load", run, { once: true });

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") run();
  });

  window.addEventListener("popstate", run);
  window.addEventListener("storage", (e) => {
    if (e.key === PENDING_KEY) run();
  });

  const hist = window.history;
  hist.pushState = function pushStatePatched() {
    const r = window.__vlvNativePushState.apply(this, arguments);
    setTimeout(run, 200);
    return r;
  };
  hist.replaceState = function replaceStatePatched() {
    const r = window.__vlvNativeReplaceState.apply(this, arguments);
    setTimeout(run, 200);
    return r;
  };
}
